<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { QSlider } from 'quasar';

import BaseResultAnswer from '@/components/base/BaseResultAnswer.vue';

import { getTranslation } from '@/composables/data-display-utils';
import type { Locales, SummaryFormStatChoiceDto, SummaryFormStatLevelItemDto } from '@/services/types/api.types';

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
  level: SummaryFormStatLevelItemDto;
}>();

// COMPUTED
const convertToMarkerLabels = (levelChoices: SummaryFormStatChoiceDto[]) => {
  return levelChoices.map((levelChoice) => ({
    value: levelChoice.id as number,
    label: getTranslation(levelChoice, 'text', props.languageEnabled),
  }));
};
</script>
<template>
  <div class="matrix-choice-item">
    <div class="matrix-choice-item__title">{{ getTranslation(level, 'text', props.languageEnabled) }}</div>
    <q-slider
      v-if="level.level_choices.length > 0"
      :model-value="level.average"
      :min="1"
      :max="level.level_choices.length"
      markers
      :marker-labels="convertToMarkerLabels(level.level_choices)"
      thumb-color="secondary"
      thumb-size="20px"
      inner-track-color="grey"
      track-color="grey"
      track-size="12px"
      label
      label-always
      readonly
      :label-value="`${t('form.summary.results.average')} = ${level?.average}/${level?.level_choices.length}`"
    />
    <base-result-answer
      v-for="choice in level.level_choices"
      :key="choice.id"
      :choice="choice"
      :language-enabled="props.languageEnabled"
    />
  </div>
</template>
<style lang="scss">
.matrix-choice-item {
  &__title {
    font-weight: bold;
    font-size: 15px;
    margin: $space-xl 0 $space-md 0;
  }

  .q-slider {
    margin-bottom: $space-md;

    &__track {
      border-radius: 10px !important;
    }

    &__thumb {
      height: 40px !important;
      width: 40px !important;

      &-shape {
        stroke-width: 0.5;

        path {
          stroke: $color-primary;
          fill: $color-neutral-white;
        }
      }
    }

    &__markers {
      color: $color-neutral-grey-200 !important;
    }

    &__text {
      color: $color-neutral-black-text;
      font-weight: bold;

      &-container {
        display: flex;
        align-items: center;
        background-color: transparent;
        z-index: 2;
      }
    }

    &__pin--h-standard:before {
      border: none !important;
    }

    &__label--h-standard {
      bottom: 0;
    }

    &__pin--h-standard {
      bottom: 75%;
    }

    &__marker-labels {
      font-size: 14px;
      color: $color-neutral-black-text;

      &:last-child {
        transform: translateX(-100%) !important;
      }

      &:nth-child(1) {
        transform: none !important;
      }

      &--h-ltr {
        white-space: nowrap;
      }
    }
  }

  &:first-child {
    .matrix-choice-item__title {
      margin-top: 0;
    }
  }
}
</style>
